<template>
  <div class="activity-logs">
    <div
      v-if="loading"
      class="text-center py-4"
    >
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">
        جاري تحميل سجل النشاطات...
      </p>
    </div>

    <div
      v-else-if="error"
      class="alert alert-danger"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="16"
        class="mr-1"
      />
      {{ error }}
    </div>

    <div
      v-else-if="logs.length === 0"
      class="text-center py-5"
    >
      <feather-icon
        icon="FileTextIcon"
        size="48"
        class="text-muted mb-2"
      />
      <p class="text-muted">
        لا توجد سجلات نشاطات
      </p>
    </div>

    <div
      v-else
      class="activity-timeline"
    >
      <div
        v-for="(log, index) in logs"
        :key="log.id"
        class="activity-log-item"
        :class="{ 'last-item': index === logs.length - 1 }"
      >
        <!-- Timeline connector -->
        <div
          v-if="index < logs.length - 1"
          class="timeline-connector"
        />

        <!-- Timeline dot -->
        <div
          class="timeline-dot"
          :class="getEventClass(log.event)"
        >
          <feather-icon
            :icon="getEventIcon(log.event)"
            size="14"
          />
        </div>

        <!-- Log content -->
        <div class="log-content">
          <div class="log-header">
            <div class="d-flex justify-content-between align-items-start flex-wrap">
              <div class="log-description">
                <strong>{{ log.description }}</strong>
              </div>
              <b-badge
                :variant="getEventBadgeVariant(log.event)"
                class="ml-2"
              >
                {{ getEventLabel(log.event) }}
              </b-badge>
            </div>
          </div>

          <div class="log-meta">
            <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
              <span class="text-muted">
                <feather-icon
                  icon="UserIcon"
                  size="14"
                  class="mr-1"
                />
                {{ (log.causer && log.causer.name) || 'مستخدم غير معروف' }}
              </span>
              <span class="text-muted">
                <feather-icon
                  icon="ClockIcon"
                  size="14"
                  class="mr-1"
                />
                {{ formatDate(log.created_at) }}
              </span>
            </div>
          </div>

          <!-- Changes display for updated events -->
          <div
            v-if="log.event === 'updated' && log.properties && log.properties.old"
            class="log-changes mt-3"
          >
            <b-card class="bg-light">
              <h6 class="mb-3">
                <feather-icon
                  icon="EditIcon"
                  size="16"
                  class="mr-1"
                />
                التغييرات:
              </h6>
              <div class="changes-list">
                <div
                  v-for="(change, index) in getFilteredChanges(log)"
                  :key="index"
                  class="change-item mb-2"
                >
                  <div class="change-field">
                    <strong>{{ getFieldLabel(change.key) }}:</strong>
                  </div>
                  <div class="change-values">
                    <div class="old-value">
                      <span class="badge badge-light-danger">قبل:</span>
                      <span class="ml-1">{{ formatValue(change.oldValue) }}</span>
                    </div>
                    <div class="new-value mt-1">
                      <span class="badge badge-light-success">بعد:</span>
                      <span class="ml-1">{{ formatValue(change.newValue) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </b-card>
          </div>

          <!-- Attributes display for created events -->
          <div
            v-if="log.event === 'created' && log.properties && log.properties.attributes"
            class="log-attributes mt-3"
          >
            <b-card class="bg-light">
              <h6 class="mb-3">
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-1"
                />
                البيانات المُنشأة:
              </h6>
              <div class="attributes-list">
                <div
                  v-for="(value, key) in log.properties.attributes"
                  :key="key"
                  class="attribute-item mb-2"
                >
                  <strong>{{ getFieldLabel(key) }}:</strong>
                  <span class="ml-2">{{ formatValue(value) }}</span>
                </div>
              </div>
            </b-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BSpinner,
  BBadge,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import activityLogsApi from '@/api/activity-logs/activityLogs'

export default {
  name: 'ActivityLogs',
  components: {
    BCard,
    BSpinner,
    BBadge,
    FeatherIcon,
  },
  props: {
    modelType: {
      type: String,
      required: true,
      default: 'fixed-point',
    },
    modelId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      logs: [],
      loading: false,
      error: null,
    }
  },
  watch: {
    modelId() {
      this.fetchLogs()
    },
  },
  mounted() {
    this.fetchLogs()
  },
  methods: {
    async fetchLogs() {
      if (!this.modelId) return

      this.loading = true
      this.error = null

      try {
        const response = await activityLogsApi.getByModel(this.modelType, this.modelId)
        if (response.data && response.data.status === 'success') {
          this.logs = response.data.data || []
        } else {
          this.logs = response.data || []
        }
      } catch (error) {
        console.error('Error fetching activity logs:', error)
        this.error = (error.response && error.response.data && error.response.data.message) || 'حدث خطأ أثناء تحميل سجل النشاطات'
        this.logs = []
      } finally {
        this.loading = false
      }
    },
    getEventClass(event) {
      const classes = {
        created: 'event-created',
        updated: 'event-updated',
        deleted: 'event-deleted',
        restored: 'event-restored',
      }
      return classes[event] || 'event-default'
    },
    getEventIcon(event) {
      const icons = {
        created: 'PlusIcon',
        updated: 'EditIcon',
        deleted: 'TrashIcon',
        restored: 'RotateCcwIcon',
      }
      return icons[event] || 'FileTextIcon'
    },
    getEventLabel(event) {
      const labels = {
        created: 'إنشاء',
        updated: 'تحديث',
        deleted: 'حذف',
        restored: 'استعادة',
      }
      return labels[event] || event
    },
    getEventBadgeVariant(event) {
      const variants = {
        created: 'success',
        updated: 'primary',
        deleted: 'danger',
        restored: 'warning',
      }
      return variants[event] || 'secondary'
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatValue(value) {
      if (value === null || value === undefined) return '-'
      if (typeof value === 'boolean') return value ? 'نعم' : 'لا'
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },
    getFieldLabel(key) {
      // Map common field names to Arabic labels
      const labels = {
        code: 'الكود',
        name: 'الاسم',
        description: 'الوصف',
        level: 'المستوى',
        accuracy_degree: 'درجة الدقة',
        responsible_employee_id: 'الموظف المسؤول',
        latitude: 'خط العرض',
        longitude: 'خط الطول',
        etm_est: 'ETM East',
        etm_north: 'ETM North',
        etm_zone: 'ETM Zone',
        utm_est: 'UTM East',
        utm_north: 'UTM North',
        utm_zone: 'UTM Zone',
        coord_type: 'نوع الإحداثيات',
        correction_type: 'نوع التصحيح',
        index: 'المنسوب',
        governorate_id: 'المحافظة',
        center_id: 'الجهاز / الحي / المركز',
        city_id: 'الوحدة المحلية / المدينة',
        district_id: 'القرية / التجمع',
        village_id: 'عزبة / نجع / منطقة',
      }
      return labels[key] || key
    },
    shouldShowChange(key, oldValue, newValue) {
      // Only show changes where values actually differ
      if (oldValue === undefined || oldValue === null) {
        return newValue !== undefined && newValue !== null
      }
      return oldValue !== newValue
    },
    getFilteredChanges(log) {
      if (!log.properties || !log.properties.attributes || !log.properties.old) {
        return []
      }
      const changes = []
      const { attributes } = log.properties
      const oldValues = log.properties.old

      Object.keys(attributes).forEach(key => {
        const oldValue = oldValues[key]
        const newValue = attributes[key]
        if (this.shouldShowChange(key, oldValue, newValue)) {
          changes.push({
            key,
            oldValue,
            newValue,
          })
        }
      })

      return changes
    },
  },
}
</script>

<style scoped>
.activity-logs {
  padding: 1rem 0;
}

.activity-timeline {
  position: relative;
  padding-left: 2rem;
}

.activity-log-item {
  position: relative;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
}

.activity-log-item.last-item {
  margin-bottom: 0;
  padding-bottom: 0;
}

.timeline-dot {
  position: absolute;
  left: -2rem;
  top: 0.25rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 2;
}

.timeline-dot.event-created {
  background-color: #28c76f;
  color: white;
}

.timeline-dot.event-updated {
  background-color: #7367f0;
  color: white;
}

.timeline-dot.event-deleted {
  background-color: #ea5455;
  color: white;
}

.timeline-dot.event-restored {
  background-color: #ff9f43;
  color: white;
}

.timeline-connector {
  position: absolute;
  left: -1.5rem;
  top: 2.25rem;
  width: 2px;
  height: calc(100% - 0.5rem);
  background-color: #e0e0e0;
  z-index: 1;
}

.log-content {
  background: #fff;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.log-header {
  margin-bottom: 0.5rem;
}

.log-description {
  flex: 1;
}

.log-meta {
  font-size: 0.875rem;
}

.log-changes,
.log-attributes {
  margin-top: 1rem;
}

.change-item {
  padding: 0.5rem;
  background: #fff;
  border-radius: 0.25rem;
  border-left: 3px solid #7367f0;
}

.change-field {
  margin-bottom: 0.5rem;
  color: #5e5873;
}

.change-values {
  font-size: 0.875rem;
}

.old-value,
.new-value {
  display: flex;
  align-items: center;
}

.attribute-item {
  padding: 0.25rem 0;
  color: #5e5873;
}

.gap-2 {
  gap: 0.5rem;
}

.badge-light-danger {
  background-color: #ffeaea;
  color: #ea5455;
}

.badge-light-success {
  background-color: #e8f5e9;
  color: #28c76f;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .activity-timeline {
    padding-left: 1.5rem;
  }

  .timeline-dot {
    left: -1.5rem;
    width: 1.5rem;
    height: 1.5rem;
  }

  .timeline-connector {
    left: -1rem;
  }

  .log-content {
    padding: 0.75rem;
  }

  .log-description strong {
    font-size: 0.9rem;
  }
}
</style>
